/**
 * Learn to Play: on your turn, the one move the script teaches (`LEARN_TURNS`), step by step, so
 * the table can lock everything else and make the next thing to tap pulse. The server refuses any
 * other move anyway (`Game.requireLearnMove`).
 *
 * Steps: before the roll, Roll. After it, ending the turn: hold the dice the script holds, then
 * End Turn. Attacking: hold the dice of the natural kill, change the die with your power, hold
 * it, pick every target, then Attack.
 */
import { LEARN_TURNS, applyMods, type DieMod, type LearnView, type PlayerId, type TurnView } from '@kadice/shared';

export type CoachStage = 'roll' | 'hold' | 'end' | 'natural' | 'power' | 'holdPower' | 'targets' | 'attack';

export interface Coach {
  stage: CoachStage;
  /** Dice that may be tapped (held or let go) now. */
  toggle: number[];
  /** The one power arrow that may be pressed now. */
  arrow: { index: number; dir: 'up' | 'down' } | null;
  /** The one turn button that may be pressed now. */
  button: 'roll' | 'end' | 'attack' | null;
  /** Cards that may be picked as targets now. */
  targets: PlayerId[];
}

const none = { toggle: [], arrow: null, button: null, targets: [] };

/** The scripted turn's next step, or null when your turn isn't one the script covers. */
export function coachFor(
  learn: LearnView,
  turn: TurnView,
  sequence: number,
  keep: readonly number[],
  mods: readonly DieMod[],
  targets: readonly PlayerId[],
): Coach | null {
  const step = LEARN_TURNS[sequence - 1];
  if (!step) return null;
  if (!turn.hasRolled) return { ...none, stage: 'roll', button: 'roll' };
  const missing = (want: readonly number[]) => want.filter((i) => !keep.includes(i));
  if (step.end) {
    const diff = [...missing(step.end), ...keep.filter((i) => !step.end!.includes(i))];
    return diff.length > 0 ? { ...none, stage: 'hold', toggle: diff } : { ...none, stage: 'end', button: 'end' };
  }
  const move = step.attack!;
  const natural = missing(move.natural);
  if (natural.length > 0) return { ...none, stage: 'natural', toggle: natural };
  const dice = applyMods(turn.pool, mods);
  const change = move.mods.find((m) => dice[m.index] !== m.value);
  if (change) {
    const dir = dice[change.index]! < change.value ? 'up' : 'down';
    return { ...none, stage: 'power', arrow: { index: change.index, dir } };
  }
  const rest = missing(move.keep);
  if (rest.length > 0) return { ...none, stage: 'holdPower', toggle: rest };
  const want = move.targets.map((r) => learn.ids[r]).filter((id) => !targets.includes(id));
  if (want.length > 0) return { ...none, stage: 'targets', targets: want };
  return { ...none, stage: 'attack', button: 'attack' };
}
